import { css } from "@emotion/css";
import type { TextProps } from "@octopusdeploy/design-system-components";
import { CopyToClipboardButton, Button, Tooltip, Callout } from "@octopusdeploy/design-system-components";
import { ArrowRefreshIcon, CopyIcon, PenLineIcon, XmarkIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { generateSlug } from "@octopusdeploy/utilities";
import { useEffect, useState } from "react";
import { TextWithLabel as Text } from "../Text";
export type SlugEditorProps = TextProps & {
    originalSlug: string;
    name?: string | undefined;
    additionalWarningMessage?: string | undefined;
    prefix?: string | undefined;
    readOnlyMessage?: string | undefined;
};
export function SlugEditor({ onChange: onChangeProp, originalSlug, name, additionalWarningMessage, prefix, readOnly, readOnlyMessage, ...rest }: SlugEditorProps) {
    const [currentValue, setCurrentValue] = useState(rest.value);
    const [validSlug, setValidSlug] = useState(rest.value);
    const [editing, setEditing] = useState(false);
    // If the original slug changes, we've likely saved, disable the editor
    useEffect(() => {
        setEditing(false);
    }, [originalSlug]);
    // Ensure any input is converted to a slug, but allowing for trailing dashes
    const onChange = (input: string) => {
        const newCurrentValue = generateSlug(input, false);
        setCurrentValue(newCurrentValue);
        // Store the current value but as a valid slug
        const newValidSlug = generateSlug(newCurrentValue);
        setValidSlug(newValidSlug);
        onChangeProp(newValidSlug);
    };
    // Once we lose focus, update the value to the valid one
    const onBlur = () => {
        onChange(validSlug);
    };
    const onEditClick = () => setEditing(true);
    const onCancelEditClick = () => {
        setEditing(false);
        onChange(originalSlug);
    };
    const onUpdateClick = () => {
        if (name) {
            const newSlug = generateSlug(name);
            onChange(newSlug);
            setEditing(true);
        }
    };
    const renderActions = () => {
        return (<div className={slugEditorStyles.editorActionList}>
                {!editing && (<Tooltip content={readOnly && readOnlyMessage ? readOnlyMessage : "Change"}>
                        <Button size={"small"} importance={"quiet"} icon={<PenLineIcon size={24} color={themeTokens.color.button.icon.secondary}/>} onClick={onEditClick} accessibleName={"Change slug"} disabled={readOnly}/>
                    </Tooltip>)}
                {editing && (<Tooltip content={"Cancel"}>
                        <Button size={"small"} importance={"quiet"} icon={<XmarkIcon size={24} color={themeTokens.color.button.icon.secondary}/>} onClick={onCancelEditClick} accessibleName={"Cancel editing slug"}/>
                    </Tooltip>)}
                {name && generateSlug(name) !== validSlug && (<Tooltip content={"Update"}>
                        <Button size={"small"} importance={"quiet"} icon={<ArrowRefreshIcon size={24} color={themeTokens.color.button.icon.secondary}/>} onClick={onUpdateClick} accessibleName={"Update slug to match name"}/>
                    </Tooltip>)}
                <div className={slugEditorStyles.copyToClipboardButtonContainer}>
                    <CopyToClipboardButton value={prefix ? `${prefix}${validSlug}` : validSlug}>
                        {/*The onclick callback is left empty on purpose so that we can use <Button> component to keep consistent UX as other buttons.*/}
                        {/*The click event is handled inside <CopyToClipboardButton> component*/}
                        <Button size={"small"} importance={"quiet"} icon={<CopyIcon size={24} color={themeTokens.color.button.icon.secondary}/>} onClick={() => { }} accessibleName={"Copy to clipboard"}/>
                    </CopyToClipboardButton>
                </div>
            </div>);
    };
    const renderPrefix = () => {
        if (prefix) {
            return {
                startAdornment: (<>
                        <code>{prefix}</code>
                        &nbsp;
                    </>),
            };
        }
        return {};
    };
    return (<>
            <div className={slugEditorStyles.slugInputAndActionList}>
                <Text {...rest} inputProps={renderPrefix()} value={currentValue} onChange={onChange} onBlur={onBlur} disabled={!editing}/>
                {renderActions()}
            </div>
            {originalSlug !== validSlug && (<Callout title={"Be careful when changing a slug"} type={"warning"}>
                    {additionalWarningMessage && (<span>
                            {additionalWarningMessage}
                            <br />
                            <br />
                        </span>)}
                    <span>Modifying a slug may impact any references to it. Any scripts referencing the old slug will need to be updated manually.</span>
                </Callout>)}
        </>);
}
const slugEditorStyles = {
    slugInputAndActionList: css({
        display: "flex",
        gap: space[8],
    }),
    editorActionList: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    copyToClipboardButtonContainer: css({
        width: "1.5rem",
        height: "1.5rem",
        display: "flex",
        alignItems: "center",
    }),
};
